import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { StatusPagePiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/status-pages')({
  component: StatusPagesPage,
  head: () => ({
    meta: [
      { title: 'Public Status Pages — Hosted & Customizable | Uptrack' },
      { name: 'description', content: 'Public status pages for your services: custom domains, subscriber notifications, widgets. 1 page on free, 5 on Pro, unlimited on Team and Business.' },
      { property: 'og:title', content: 'Public Status Pages — Hosted & Customizable | Uptrack' },
      { property: 'og:description', content: 'Hosted status pages with custom domains and subscriber notifications. Status pages are included.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-status-pages.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/status-pages' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'Status Pages', url: 'https://uptrack.app/features/status-pages' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack Status Pages',
        description: 'Public status pages with custom domains, subscriber notifications, and embeddable widgets.',
        url: 'https://uptrack.app/features/status-pages',
      })),
    ],
  }),
})

const competitors = [
  { name: 'Statuspage (Atlassian)', freePages: 'No free plan', customDomain: 'Paid', subscribers: 'Paid' },
  { name: 'Better Stack', freePages: '1 on free plan', customDomain: 'Paid', subscribers: 'Yes' },
  { name: 'UptimeRobot', freePages: '1 on free plan', customDomain: 'Paid', subscribers: 'No' },
  { name: 'Uptrack', freePages: '1 on free, 5 on Pro, unlimited on Team', customDomain: 'Team and Business', subscribers: 'Paid plans', highlight: true },
]

function StatusPagesPage() {
  return (
    <FeaturePage
      section="Public status pages"
      title="Status pages your users can trust."
      intro="Create public status pages with custom domains, subscriber notifications, and embeddable widgets. Status pages are included on every plan; custom domains and subscriber notifications are on paid plans."
      featuresHeading="Professional status pages, zero setup"
      features={[
        { title: 'Custom domain support', desc: 'Host your status page on status.yourdomain.com. Point a CNAME and it just works.' },
        { title: 'Subscriber notifications', desc: 'Users subscribe to your status page and get notified on incidents automatically. Build trust through transparency.' },
        { title: 'Embeddable widgets', desc: 'Embed a status badge or incident feed on your site. Keep users informed without sending them to another page.' },
        { title: 'Status pages included', desc: 'Create up to 5 status pages on Pro. Group monitors by product, team, or environment.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Dedicated status page tools charge $29+/mo. Uptrack includes them with monitoring.',
        columns: ['Provider', 'Included pages', 'Custom Domain', 'Subscribers'],
        rows: competitors.map((c) => ({ cells: [c.name, c.freePages, c.customDomain, c.subscribers], highlight: c.highlight })),
      }}
      why={{
        heading: 'Why status pages matter',
        body: 'When your service goes down, users check your status page before filing a support ticket. A clear, up-to-date status page reduces support volume, builds trust, and shows you take reliability seriously. Without one, users assume the worst.',
      }}
      cta={{ heading: 'Create your status page now', body: 'Every plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="status.example.com">
          <StatusPagePiece />
        </ProductFrame>
      }
    />
  )
}
